/**
 * DataRoomPanel - Document checklist and data room management
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { CompanyData } from '../../types'

interface DataRoomPanelProps {
  company: CompanyData | null
}

interface Document {
  id: string
  name: string
  category: string
  priority: 'critical' | 'high' | 'medium' | 'low'
  status: 'received' | 'pending' | 'requested' | 'na'
  notes?: string
  file?: File
}

// Standard checklist for public companies (have SEC filings, etc.)
const publicDocumentChecklist: Document[] = [
  { id: '1', name: 'Audited Financial Statements (3Y)', category: 'Financial', priority: 'critical', status: 'received' },
  { id: '2', name: 'Management Accounts (YTD)', category: 'Financial', priority: 'critical', status: 'received' },
  { id: '3', name: 'Revenue Breakdown by Product/Segment', category: 'Financial', priority: 'high', status: 'pending' },
  { id: '4', name: 'Customer Contracts (Top 10)', category: 'Commercial', priority: 'critical', status: 'requested' },
  { id: '5', name: 'Employee List with Titles/Tenure', category: 'HR', priority: 'high', status: 'pending' },
  { id: '6', name: 'IP Portfolio Summary', category: 'Legal', priority: 'high', status: 'received' },
  { id: '7', name: 'Material Litigation Summary', category: 'Legal', priority: 'critical', status: 'na' },
  { id: '8', name: 'Tax Returns (3Y)', category: 'Tax', priority: 'high', status: 'pending' },
  { id: '9', name: 'Technology Architecture Diagram', category: 'IT', priority: 'medium', status: 'requested' },
  { id: '10', name: 'Insurance Policies Summary', category: 'Risk', priority: 'medium', status: 'pending' },
]

// Comprehensive checklist for private companies (no public filings available)
const privateDocumentChecklist: Document[] = [
  // Financial - Critical (no public data available)
  { id: 'p1', name: 'Audited Financial Statements (3-5 years)', category: 'Financial', priority: 'critical', status: 'pending' },
  { id: 'p2', name: 'Management Accounts (YTD + Monthly)', category: 'Financial', priority: 'critical', status: 'pending' },
  { id: 'p3', name: 'Bank Statements (12 months)', category: 'Financial', priority: 'critical', status: 'pending' },
  { id: 'p4', name: 'Revenue Breakdown by Product/Segment', category: 'Financial', priority: 'critical', status: 'pending' },
  { id: 'p5', name: 'Detailed P&L with Cost Breakdown', category: 'Financial', priority: 'high', status: 'pending' },
  { id: 'p6', name: 'Working Capital Analysis', category: 'Financial', priority: 'high', status: 'pending' },
  { id: 'p7', name: 'Debt Schedule & Covenants', category: 'Financial', priority: 'high', status: 'pending' },
  { id: 'p8', name: 'Cash Flow Projections (3-5 years)', category: 'Financial', priority: 'high', status: 'pending' },

  // Ownership & Governance
  { id: 'p9', name: 'Cap Table / Ownership Structure', category: 'Governance', priority: 'critical', status: 'pending' },
  { id: 'p10', name: 'Shareholder Agreements', category: 'Governance', priority: 'critical', status: 'pending' },
  { id: 'p11', name: 'Board Minutes (Last 2 years)', category: 'Governance', priority: 'high', status: 'pending' },
  { id: 'p12', name: 'Organizational Chart', category: 'Governance', priority: 'high', status: 'pending' },

  // Commercial
  { id: 'p13', name: 'Customer Contracts (Top 10)', category: 'Commercial', priority: 'critical', status: 'pending' },
  { id: 'p14', name: 'Customer Concentration Analysis', category: 'Commercial', priority: 'critical', status: 'pending' },
  { id: 'p15', name: 'Supplier/Vendor Agreements', category: 'Commercial', priority: 'high', status: 'pending' },
  { id: 'p16', name: 'Sales Pipeline & Backlog', category: 'Commercial', priority: 'medium', status: 'pending' },

  // HR
  { id: 'p17', name: 'Key Management Employment Agreements', category: 'HR', priority: 'critical', status: 'pending' },
  { id: 'p18', name: 'Employee List with Titles/Tenure', category: 'HR', priority: 'high', status: 'pending' },
  { id: 'p19', name: 'Compensation & Benefits Summary', category: 'HR', priority: 'medium', status: 'pending' },

  // Legal
  { id: 'p20', name: 'IP Portfolio / Patents', category: 'Legal', priority: 'high', status: 'pending' },
  { id: 'p21', name: 'Pending Litigation Summary', category: 'Legal', priority: 'critical', status: 'pending' },
  { id: 'p22', name: 'Material Contracts', category: 'Legal', priority: 'high', status: 'pending' },
  { id: 'p23', name: 'Regulatory Licenses & Permits', category: 'Legal', priority: 'medium', status: 'pending' },

  // Tax
  { id: 'p24', name: 'Tax Returns (3-5 years)', category: 'Tax', priority: 'high', status: 'pending' },
  { id: 'p25', name: 'Tax Correspondence with Authorities', category: 'Tax', priority: 'medium', status: 'pending' },

  // IT & Operations
  { id: 'p26', name: 'Technology Architecture Diagram', category: 'IT', priority: 'medium', status: 'pending' },
  { id: 'p27', name: 'IT Systems & Software Inventory', category: 'IT', priority: 'medium', status: 'pending' },

  // Risk
  { id: 'p28', name: 'Insurance Policies (Full Schedule)', category: 'Risk', priority: 'high', status: 'pending' },
  { id: 'p29', name: 'Environmental Assessments', category: 'Risk', priority: 'medium', status: 'pending' },
  { id: 'p30', name: 'Real Estate Leases & Deeds', category: 'Risk', priority: 'medium', status: 'pending' },
]

// Default to public checklist (will be overridden by component based on company type)
const documentChecklist = publicDocumentChecklist

const publicCategoryOptions = ['Financial', 'Commercial', 'HR', 'Legal', 'Tax', 'IT', 'Risk', 'Other']
const privateCategoryOptions = ['Financial', 'Governance', 'Commercial', 'HR', 'Legal', 'Tax', 'IT', 'Risk', 'Other']

export function DataRoomPanel({ company }: DataRoomPanelProps) {
  // Detect if company is private
  const isPrivate = company?.isPrivate || company?.quoteType === 'PRIVATE'

  // Use appropriate document checklist based on company type
  const initialChecklist = isPrivate ? privateDocumentChecklist : publicDocumentChecklist
  const categoryOptions = isPrivate ? privateCategoryOptions : publicCategoryOptions

  const [documents, setDocuments] = useState<Document[]>(initialChecklist)
  const [filter, setFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Add document form state
  const [newDocName, setNewDocName] = useState('')
  const [newDocCategory, setNewDocCategory] = useState('Financial')
  const [newDocPriority, setNewDocPriority] = useState<Document['priority']>('medium')
  const [newDocFile, setNewDocFile] = useState<File | null>(null)

  // Reset document list when company type changes
  useEffect(() => {
    const newChecklist = isPrivate ? privateDocumentChecklist : publicDocumentChecklist
    setDocuments(newChecklist)
    setFilter('all')
  }, [company?.ticker, isPrivate])

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to manage data room documents</p>
      </div>
    )
  }

  const updateStatus = (id: string, status: Document['status']) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, status } : doc))
    )
  }

  const filteredDocs = filter === 'all' ? documents : documents.filter((d) => d.category === filter)
  const categories = [...new Set(documents.map((d) => d.category))]

  const stats = {
    total: documents.length,
    received: documents.filter((d) => d.status === 'received').length,
    pending: documents.filter((d) => d.status === 'pending').length,
    requested: documents.filter((d) => d.status === 'requested').length,
  }

  const completionRate = Math.round((stats.received / (stats.total - documents.filter((d) => d.status === 'na').length)) * 100)

  // Handle file selection
  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0]
      setNewDocFile(file)
      if (!newDocName) {
        // Auto-fill name from filename
        setNewDocName(file.name.replace(/\.[^/.]+$/, ''))
      }
    }
  }

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
    setShowAddModal(true)
  }

  // Add new document
  const handleAddDocument = useCallback(() => {
    if (!newDocName.trim()) return

    const newDoc: Document = {
      id: `doc-${Date.now()}`,
      name: newDocName.trim(),
      category: newDocCategory,
      priority: newDocPriority,
      status: newDocFile ? 'received' : 'pending',
      file: newDocFile || undefined,
    }

    setDocuments(prev => [...prev, newDoc])
    setShowAddModal(false)

    // Reset form
    setNewDocName('')
    setNewDocCategory('Financial')
    setNewDocPriority('medium')
    setNewDocFile(null)
  }, [newDocName, newDocCategory, newDocPriority, newDocFile])

  // Delete document
  const handleDeleteDocument = (id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id))
  }

  return (
    <div
      className="space-y-6"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-term-primary/20 border-4 border-dashed border-term-primary z-50 flex items-center justify-center">
          <div className="bg-term-bg p-8 rounded-lg text-center">
            <div className="text-4xl mb-4">📁</div>
            <div className="text-xl text-term-text font-semibold">Drop file to upload</div>
            <div className="text-term-textDim">The file will be added to the data room</div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="term-box p-6 w-full max-w-md bg-term-bg">
            <h3 className="text-lg font-semibold text-term-text mb-4">Add Document</h3>

            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 mb-4 text-center cursor-pointer transition-colors ${
                newDocFile
                  ? 'border-term-success bg-term-success/10'
                  : 'border-term-border hover:border-term-primary'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files)}
              />
              {newDocFile ? (
                <>
                  <div className="text-term-success text-2xl mb-2">✓</div>
                  <div className="text-term-text font-medium">{newDocFile.name}</div>
                  <div className="text-term-textDim text-sm">
                    {(newDocFile.size / 1024).toFixed(1)} KB
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setNewDocFile(null)
                    }}
                    className="text-term-error text-sm mt-2 hover:underline"
                  >
                    Remove file
                  </button>
                </>
              ) : (
                <>
                  <div className="text-term-textDim text-2xl mb-2">📤</div>
                  <div className="text-term-text">Click to upload or drag & drop</div>
                  <div className="text-term-textDim text-sm">PDF, DOC, XLS, or images</div>
                </>
              )}
            </div>

            {/* Document Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-term-textDim mb-1">Document Name</label>
              <input
                type="text"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="e.g., Q3 Financial Report"
                className="term-input w-full"
              />
            </div>

            {/* Category */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-term-textDim mb-1">Category</label>
              <select
                value={newDocCategory}
                onChange={(e) => setNewDocCategory(e.target.value)}
                className="term-input w-full"
              >
                {categoryOptions.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Priority */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-term-textDim mb-2">Priority</label>
              <div className="flex gap-2">
                {(['critical', 'high', 'medium', 'low'] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setNewDocPriority(p)}
                    className={`px-3 py-1 text-sm rounded border capitalize transition-colors ${
                      newDocPriority === p
                        ? p === 'critical' ? 'border-term-error bg-term-error/20 text-term-error'
                        : p === 'high' ? 'border-term-warning bg-term-warning/20 text-term-warning'
                        : p === 'medium' ? 'border-term-info bg-term-info/20 text-term-info'
                        : 'border-term-textDim bg-term-bgDark text-term-textDim'
                        : 'border-term-border text-term-textDim hover:border-term-primary/50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setNewDocName('')
                  setNewDocFile(null)
                }}
                className="term-button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDocument}
                disabled={!newDocName.trim()}
                className="term-button term-button-primary disabled:opacity-50"
              >
                Add Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Private Company Banner */}
      {isPrivate && (
        <div className="bg-term-info/20 border border-term-info rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="text-term-info font-bold text-lg">
                Private Company - Comprehensive Document Request
              </h3>
              <p className="text-term-textDim text-sm">
                Since {company.name} is a private company, no public filings are available. This enhanced
                checklist includes all documents typically required for a thorough due diligence process.
              </p>
              <p className="text-term-info text-sm mt-1">
                💡 <span className="font-medium">{documents.length} documents</span> across {categories.length} categories
                to request from the target company.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          📁 Data Room - {company.ticker}
          {isPrivate && <span className="ml-2 px-2 py-0.5 bg-term-info/30 text-term-info text-xs rounded font-bold">PRIVATE</span>}
        </h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="term-button term-button-primary"
        >
          + Add Document
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard label="Total Documents" value={stats.total} />
        <StatCard label="Received" value={stats.received} color="success" />
        <StatCard label="Pending" value={stats.pending} color="warning" />
        <StatCard label="Requested" value={stats.requested} color="info" />
        <div className="term-box p-4">
          <div className="text-term-textDim text-xs mb-1">Completion</div>
          <div className="text-2xl font-bold text-term-primary">{completionRate}%</div>
          <div className="term-progress h-1.5 mt-2">
            <div
              className="term-progress-bar bg-term-primary"
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`term-tab ${filter === 'all' ? 'term-tab-active' : ''}`}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`term-tab ${filter === cat ? 'term-tab-active' : ''}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Document List */}
      <div className="term-box">
        <table className="term-table">
          <thead>
            <tr>
              <th>Document</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDocs.map((doc) => (
              <tr key={doc.id}>
                <td className="font-medium">{doc.name}</td>
                <td className="text-term-textDim">{doc.category}</td>
                <td>
                  <PriorityBadge priority={doc.priority} />
                </td>
                <td>
                  <StatusBadge status={doc.status} />
                </td>
                <td>
                  <div className="flex gap-2">
                    <select
                      value={doc.status}
                      onChange={(e) => updateStatus(doc.id, e.target.value as Document['status'])}
                      className="term-input py-1 text-xs"
                    >
                      <option value="pending">Pending</option>
                      <option value="requested">Requested</option>
                      <option value="received">Received</option>
                      <option value="na">N/A</option>
                    </select>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="text-term-error hover:underline text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface StatCardProps {
  label: string
  value: number
  color?: 'success' | 'warning' | 'info' | 'error'
}

function StatCard({ label, value, color }: StatCardProps) {
  const colorClasses = {
    success: 'text-term-success',
    warning: 'text-term-warning',
    info: 'text-term-info',
    error: 'text-term-error',
  }

  return (
    <div className="term-box p-4">
      <div className="text-term-textDim text-xs mb-1">{label}</div>
      <div className={`text-2xl font-bold ${color ? colorClasses[color] : 'text-term-text'}`}>
        {value}
      </div>
    </div>
  )
}

interface PriorityBadgeProps {
  priority: Document['priority']
}

function PriorityBadge({ priority }: PriorityBadgeProps) {
  const config = {
    critical: { label: 'CRITICAL', class: 'term-badge-error' },
    high: { label: 'HIGH', class: 'term-badge-warning' },
    medium: { label: 'MEDIUM', class: 'term-badge-info' },
    low: { label: 'LOW', class: 'text-term-textDim bg-term-bgDark' },
  }

  const { label, class: className } = config[priority]

  return <span className={`term-badge ${className}`}>{label}</span>
}

interface StatusBadgeProps {
  status: Document['status']
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = {
    received: { label: 'Received', class: 'term-badge-success' },
    pending: { label: 'Pending', class: 'term-badge-warning' },
    requested: { label: 'Requested', class: 'term-badge-info' },
    na: { label: 'N/A', class: 'text-term-textDim bg-term-bgDark' },
  }

  const { label, class: className } = config[status]

  return <span className={`term-badge ${className}`}>{label}</span>
}
